import {
  CalendarIcon,
  ClipboardDocumentListIcon,
  FolderIcon,
  UserIcon,
} from '@heroicons/react/24/outline';
import { memo } from 'react';

import {
  enhancedApi,
  type ExternalTaskDetailSchema,
  type ExternalTaskRetrieveApiArg,
} from '@/services/api/generated/externalTaskService';
import { formatTaskDate } from '@/utils/taskDateUtils';

import { createEntityLink } from '../core/createEntityLink';
import type { EntityTooltipContentProps } from '../core/types';
import {
  createStatusBadge,
  EmptyState,
  ErrorState,
  InfoRow,
  LoadingState,
  TooltipHeader,
  TooltipSection,
} from '../primitives';
import { MatterLink } from './MatterLink';

// ============================================================================
// Tooltip Content
// ============================================================================

const ExternalTaskTooltipContent = memo<
  EntityTooltipContentProps<ExternalTaskDetailSchema>
>(({ fetchState }) => {
  if (fetchState.status === 'pending') {
    return <LoadingState rows={2} />;
  }

  if (fetchState.status === 'error') {
    return <ErrorState error={fetchState.error} entityType="external task" />;
  }

  if (fetchState.status === 'idle' || !fetchState.data) {
    return <EmptyState entityType="external task" />;
  }

  const task = fetchState.data;
  const { name, status, due_date, assignee, matter_id, matter_name } = task;

  return (
    <div className="divide-y divide-gray-100">
      <TooltipHeader
        icon={ClipboardDocumentListIcon}
        title={name || 'External Task'}
      />

      <TooltipSection withBorder>
        <div className="mb-2 flex items-center justify-between gap-2">
          {status && createStatusBadge({ value: status.replace('_', ' ') })}
        </div>

        {matter_id && (
          <InfoRow
            icon={FolderIcon}
            label="Matter"
            value={
              <MatterLink entityId={matter_id} side="left" className="text-xs">
                {matter_name || 'Unknown Matter'}
              </MatterLink>
            }
          />
        )}
        {due_date && (
          <InfoRow
            icon={CalendarIcon}
            label="Due Date"
            value={formatTaskDate(due_date)}
          />
        )}
        {assignee && (
          <InfoRow
            icon={UserIcon}
            label="Assigned to"
            value={assignee.name || 'Unknown'}
          />
        )}
      </TooltipSection>
    </div>
  );
});

ExternalTaskTooltipContent.displayName = 'ExternalTaskTooltipContent';

// ============================================================================
// Component
// ============================================================================

export const ExternalTaskLink = createEntityLink<
  ExternalTaskDetailSchema,
  ExternalTaskRetrieveApiArg
>({
  tooltipContent: ExternalTaskTooltipContent,
  getDetailUrl: (id) => `/external-tasks/${id}`,
  useQuery: enhancedApi.endpoints.externalTaskRetrieve.useQuery,
  getQueryArg: (id) => ({ id }),
  displayName: 'ExternalTaskLink',
});
